/**
 * Project catalogue.
 *
 * Every entry is a short case study: what the problem was, how it was approached
 * and what shipped. These are production systems built for employers and
 * clients — no client code, credentials or proprietary detail is reproduced
 * here. Entries render a generated editorial cover driven by the `accent` field.
 */

export const projectCategories = [
	"All",
	"Platform",
	"Data & Automation",
	"Product",
	"Marketing Site",
];

export const projects = [
	{
		slug: "path3-attribution-engine",
		title: "Ad-Level Attribution Engine",
		category: "Data & Automation",
		year: "2025",
		role: "Lead Engineer",
		client: "Path3 Media",
		accent: "azure",
		featured: true,
		summary:
			"Rebuilt daily performance reporting from campaign-aggregate to ad-level attribution across three ad platforms, removing duplicated spend and making per-creative optimisation possible for the first time.",
		problem:
			"Reporting rolled up at the campaign level. That is a convenient shape for a dashboard and a useless one for a decision: when a campaign contains fifteen creatives, a good CPME hides the four that are burning money. Worse, the aggregate pulls from Meta, LinkedIn and Simpli.fi overlapped, so the same spend appeared more than once in the warehouse and every downstream number inherited the error.",
		approach:
			"I moved the ingest boundary down a level — from campaign to ad — and made the write path idempotent so a retried or replayed pull collapses onto the same row rather than adding a new one. Each platform got an adapter that normalises its own idea of a day, a currency and a creative into one internal shape, with the platform's quirks quarantined at the edge instead of leaking into the model. Backfills run through the same code path as live pulls, so historical corrections are a re-run rather than a migration.",
		outcome:
			"Duplicated spend rows were eliminated and CPME/CPI became measurable per creative. Media buyers could finally see which individual ad was carrying a campaign and which was being subsidised by it.",
		highlights: [
			"Ad-level ingest across three platforms",
			"Idempotent writes and replayable backfills",
			"Per-platform adapters, one internal model",
			"Duplicated spend rows eliminated",
		],
		stack: [
			"TypeScript",
			"Node.js",
			"Strapi",
			"PostgreSQL",
			"Meta / LinkedIn / Simpli.fi APIs",
			"CRON",
		],
		links: {},
		images: [],
	},
	{
		slug: "path3-budget-optimizer",
		title: "Autonomous Budget Optimiser",
		category: "Data & Automation",
		year: "2025",
		role: "Lead Engineer",
		client: "Path3 Media",
		accent: "electric",
		featured: true,
		summary:
			"A daily optimiser that reallocates advertising budget across Meta, LinkedIn and Simpli.fi on CPME/CPI — shipped behind a SHADOW→LIVE rollout with a full audit trail per run.",
		problem:
			"Budget reallocation was a human ritual: someone read yesterday's numbers each morning and moved money. It was slow, inconsistent between operators, and systematically biased toward whatever had performed well most recently — which is exactly how you starve a new creative before it has enough data to prove itself.",
		approach:
			"The optimiser scores budget groups on CPME and CPI and reallocates within scope, treating OPEN and CLOSED groups differently. Raw daily performance is far too noisy to act on directly, so scores run through EMA smoothing. Exploration and diversity floors guarantee that nothing is zeroed out before it has been given a fair hearing, dark-horse rotations deliberately spend into under-tested options, and per-platform min/max clamps keep any single move from being catastrophic. Crucially it shipped in SHADOW first: for weeks it wrote the decisions it would have made without touching a budget, so its judgement could be audited against the operators' before it was trusted with real money.",
		outcome:
			"An autonomous daily allocation loop with every run fully reconstructable from its audit trail — which decision was made, on what inputs, and what it would have done otherwise.",
		highlights: [
			"SHADOW→LIVE phased rollout",
			"EMA smoothing over noisy daily data",
			"Exploration floors and dark-horse rotation",
			"Per-run audit trail",
		],
		stack: [
			"TypeScript",
			"Node.js",
			"Strapi",
			"Meta / LinkedIn / Simpli.fi APIs",
			"PostgreSQL",
			"Celery",
		],
		links: {},
		images: [],
	},
	{
		slug: "path3-crm-audience-sync",
		title: "Multi-CRM Audience Sync",
		category: "Data & Automation",
		year: "2025",
		role: "Lead Engineer",
		client: "Path3 Media",
		accent: "onyx",
		featured: false,
		summary:
			"A segment-sync poller spanning HubSpot, GoHighLevel and the ad platforms, with a nightly re-resync that reconciles every segment and every audience against the source of truth.",
		problem:
			"Audiences drift. A contact unsubscribes in the CRM but stays targetable on Meta for another week; a segment definition changes and nobody re-pushes it. Event-driven sync alone cannot fix this, because the failure mode is a missed event — and a missed event is invisible by definition.",
		approach:
			"Two layers. A polling runner — the v2 dbquery execution path — picks up segment changes continuously, and a nightly re-resync re-pulls every segment and re-pushes every audience wholesale, diffing against the last known state to capture adds, edits and removals regardless of whether the incremental path saw them. Per-channel error isolation means a failing GoHighLevel token does not prevent the Meta push from completing.",
		outcome:
			"Audience state that converges on correct every night rather than drifting silently, and multi-platform deployment, go-dark and resync that no longer require a human in the loop.",
		highlights: [
			"Incremental poller plus nightly full reconciliation",
			"HubSpot and GoHighLevel CRM integration",
			"Per-channel error isolation",
			"Automated go-dark and deployment",
		],
		stack: [
			"Python",
			"PostgreSQL",
			"HubSpot API",
			"GoHighLevel API",
			"CRON",
			"OAuth 2.0",
		],
		links: {},
		images: [],
	},
	{
		slug: "lepta-collaboration-platform",
		title: "Lepta — Real-Time Collaboration",
		category: "Platform",
		year: "2026",
		role: "Senior Full-Stack Engineer",
		client: "Lepta",
		accent: "azure",
		featured: true,
		summary:
			"An installable collaboration PWA with live document presence, a typed WebSocket hub, three-tier RBAC and native payments.",
		problem:
			"Collaboration products fail in the gap between tabs. A comment lands while you are elsewhere in the app and is never seen; a mention arrives while you are offline and arrives nowhere at all; two people edit the same document with no idea the other is there.",
		approach:
			"A WebSocket hub carries presence, cursors, comments and reactions over typed pub/sub channels, with viewer roles recomputed at broadcast time so a permission change takes effect on the next message rather than the next refresh. Notification delivery fans out across in-app, push and email — each channel isolated so a bounced email cannot swallow the push. The client is an installable PWA with a service worker and Web Push subscriptions, plus a dedicated @mentions inbox carrying per-tab unread counts. Access control runs organisation → workspace → project, enforced server-side, with a usePermissions hook mirroring it on the client purely for rendering, never for authority.",
		outcome:
			"A collaboration surface where presence is live, mentions reach people who are not looking at the tab, and permissions are a server fact rather than a client suggestion. Along the way I traced a hang in the re-extract endpoint to a single-worker uvicorn deployment calling its own public base URL — the worker was waiting on itself — and resolved it by short-circuiting to a direct disk read.",
		highlights: [
			"Typed WebSocket pub/sub with live presence",
			"Installable PWA with Web Push",
			"Org → workspace → project RBAC",
			"Native checkout and subscription payments",
			"Resolved a uvicorn self-loopback deadlock",
		],
		stack: [
			"FastAPI",
			"Python",
			"Next.js",
			"TypeScript",
			"WebSockets",
			"Service Workers",
			"PostgreSQL",
		],
		links: {},
		images: [],
	},
	{
		slug: "doctorev-telehealth",
		title: "DoctoRev — Telehealth Platform",
		category: "Platform",
		year: "2025",
		role: "Full-Stack Engineer",
		client: "MEDIREVS",
		accent: "onyx",
		featured: true,
		summary:
			"A healthcare platform spanning seven participant roles — patients, doctors, nurses, pharmacies, hospitals, labs and admins — with appointments, prescriptions, referrals, lab orders and video consultation.",
		problem:
			"Healthcare software is a role problem before it is a feature problem. A referral is one object that a doctor writes, a second doctor accepts, a hospital schedules and a patient can see only part of. Model that as a single permission flag and you will spend the rest of the project patching leaks.",
		approach:
			"Seven first-class roles with JWT authentication and authorisation evaluated per resource rather than per route. The API is FastAPI on async SQLAlchemy over PostgreSQL, so a consultation that touches appointments, prescriptions and notifications stays within one transaction boundary. Referrals, lab requests and pharmacy orders are modelled as state machines with explicit transitions — there is no path from ordered to resulted that does not pass through the states in between. Video consultation runs on WebRTC, with a React Native client for patients and clinicians on mobile and a Next.js surface for facilities.",
		outcome:
			"A platform where every clinical artefact — appointment, prescription, referral, lab result, pharmacy order — has a traceable lifecycle and an audience that was decided deliberately rather than inherited.",
		highlights: [
			"Seven roles with per-resource authorisation",
			"Async SQLAlchemy over PostgreSQL",
			"Referrals and lab orders as state machines",
			"WebRTC video consultation",
			"React Native and Next.js clients",
		],
		stack: [
			"FastAPI",
			"SQLAlchemy",
			"PostgreSQL",
			"Next.js",
			"TypeScript",
			"React Native",
			"WebRTC",
		],
		links: {},
		images: [],
	},
	{
		slug: "massenergize-campaigns",
		title: "MassEnergize — Climate Campaigns",
		category: "Platform",
		year: "2024",
		role: "Frontend Engineer",
		client: "MassEnergize",
		accent: "azure",
		featured: true,
		summary:
			"A multi-tenant campaign platform where many communities run concurrent climate campaigns from isolated workspaces on shared infrastructure.",
		problem:
			"Every community wants its own space, its own branding and its own approval chain. None of them want to pay for its own deployment. Multi-tenancy is the obvious answer and the easy way to leak one community's data into another's dashboard.",
		approach:
			"I built the frontend as a system rather than a set of screens: a component library that standardised campaign creation, reporting and administrative workflows, so a new feature inherited its layout, validation and empty states instead of reinventing them. The administrative portals cover role-based access control, organisation and community administration, user permissions, campaign approvals, audit history and operational reporting — each written once and parameterised by tenant rather than forked per community. Dashboards are production-grade and responsive, because the people running these campaigns are volunteers on phones, not analysts on monitors.",
		outcome:
			"Communities manage isolated campaign workspaces while sharing one platform, and the cost of adding a new feature fell sharply because the shared layer absorbed most of it.",
		highlights: [
			"Multi-tenant isolated workspaces",
			"Reusable component architecture",
			"RBAC and campaign approval flows",
			"Audit history and operational reporting",
		],
		stack: ["React", "JavaScript", "Redux", "REST APIs", "Responsive UI"],
		links: {},
		images: [],
	},
	{
		slug: "skilledhq-marketplace-ops",
		title: "SkilledHQ — Marketplace Operations",
		category: "Platform",
		year: "2022",
		role: "Software Engineer",
		client: "SkilledHQ LLC",
		accent: "electric",
		featured: false,
		summary:
			"The moderation, payout and notification machinery behind a multi-vendor marketplace — including a 15-template transactional email system and FCM push.",
		problem:
			"A marketplace is a trust system wearing a storefront. Vendors need to be held to a standard without being arbitrarily cut off, payouts need review without becoming a bottleneck, and buyers need to know where their order is without emailing anyone.",
		approach:
			"Moderation was built so that every action carries a reason: an admin can deactivate a product but must say why, the vendor receives a message they can actually act on, and reactivation is blocked until an admin has reviewed the fix — removing the silent-deactivation failure mode entirely. Payouts run through an approval workflow with a review dashboard, cancel and retry mechanics, and automatic tier promotion once a vendor accumulates successful payouts. Order-lifecycle communication runs on 15+ Brevo templates dispatched through Celery, covering confirmation, shipping, delivery, payout and the RTO path, with FCM and the Web Push API handling real-time status changes. Analytics were isolated by environment so staging traffic never polluted production numbers.",
		outcome:
			"Marketplace operations that scale with vendor count rather than admin headcount, and buyers who are informed by the system instead of by support.",
		highlights: [
			"Reason-required product moderation",
			"Payout approval with cancel/retry and tier promotion",
			"15+ transactional email templates via Celery",
			"FCM and Web Push order notifications",
		],
		stack: [
			"Django",
			"Python",
			"Celery",
			"PostgreSQL",
			"Brevo",
			"FCM",
			"Web Push API",
		],
		links: {},
		images: [],
	},
	{
		slug: "calorie-check",
		title: "Calorie Check — AI Nutrition",
		category: "Product",
		year: "2026",
		role: "Full-Stack Engineer",
		client: "Independent",
		accent: "electric",
		featured: true,
		summary:
			"A nutrition tracker that reads a barcode in the browser and falls back to a language model when the product is not in any database.",
		problem:
			"Food logging fails on friction. Every gram typed by hand is a chance to abandon the habit, and barcode databases — however large — always miss the local brand, the market stall, the thing cooked at home.",
		approach:
			"Scanning runs entirely client-side with ZXing against the device camera, so the common case never leaves the browser. When a barcode resolves to nothing, the request falls through to a language model, which estimates macros from a description and returns a structured result that is validated before it is ever stored — a model is allowed to guess, but it is not allowed to write an unvalidated row. Persistence is Prisma over a swappable adapter layer so the same schema runs on SQLite locally and PostgreSQL in production, and Web Push handles logging reminders.",
		outcome:
			"Logging a meal takes a scan or a sentence. The database handles what it knows, the model handles the long tail, and the schema never sees anything it has not checked.",
		highlights: [
			"Client-side barcode scanning with ZXing",
			"LLM macro estimation with validated output",
			"Prisma across SQLite and PostgreSQL adapters",
			"Web Push reminders",
		],
		stack: [
			"Next.js",
			"TypeScript",
			"Prisma",
			"PostgreSQL",
			"ZXing",
			"React Query",
		],
		links: {},
		images: [],
	},
	{
		slug: "true-vine-radio",
		title: "True Vine Radio",
		category: "Marketing Site",
		year: "2026",
		role: "Frontend Engineer",
		client: "Annologic",
		accent: "onyx",
		featured: false,
		summary:
			"A broadcast identity online — scroll-choreographed, motion-led, and built so that the audio never stops when you navigate.",
		problem:
			"Radio stations online usually get a page with a player bolted to the corner. Navigate away and the stream dies, which is the one thing a radio site must never do.",
		approach:
			"Built on Next.js with the player hoisted above the routing boundary so audio is continuous across navigation. The scroll experience runs on Lenis for smoothing, with GSAP driving the timeline-based sequences and Framer Motion handling component-level entrances — three tools with clearly separated jobs rather than three tools fighting over the same element. Everything respects prefers-reduced-motion, degrading to plain fades.",
		outcome:
			"A station site that behaves like a broadcast rather than a brochure, with motion that carries the brand instead of merely decorating it.",
		highlights: [
			"Persistent audio across route changes",
			"Lenis smooth scroll with GSAP timelines",
			"Framer Motion component choreography",
			"Reduced-motion fallbacks throughout",
		],
		stack: ["Next.js", "React", "GSAP", "Lenis", "Framer Motion", "Tailwind"],
		links: {},
		images: [],
	},
	{
		slug: "martin-ranch",
		title: "Martin Ranch",
		category: "Marketing Site",
		year: "2025",
		role: "Frontend Engineer",
		client: "Annologic",
		accent: "paper",
		featured: false,
		summary:
			"A business site for a working ranch — image-heavy, quiet, and fast on a rural connection.",
		problem:
			"The subject is photographic, so the images have to be large. The audience is rural, so the page has to be small. Those two facts are in direct opposition, and most sites resolve them by quietly ignoring the second.",
		approach:
			"Next.js image optimisation with correctly sized responsive sources and modern formats, so a phone on a weak connection never downloads a desktop asset. Above-the-fold imagery is prioritised and everything below it is deferred. Motion is restrained — slow fades and parallax rather than anything that competes with the photography — and the component layer is built on class-variance-authority so variants are declared once rather than accumulating as one-off overrides.",
		outcome:
			"A site that looks expensive and loads cheaply, which is the only combination that works for this audience.",
		highlights: [
			"Responsive image pipeline with modern formats",
			"Prioritised above-the-fold loading",
			"Restrained, photography-first motion",
			"Variant-driven component layer",
		],
		stack: ["Next.js", "React", "Tailwind", "Framer Motion", "CVA"],
		links: {},
		images: [],
	},
];

export const featuredProjects = projects.filter((p) => p.featured);

export const getProjectBySlug = (slug) =>
	projects.find((project) => project.slug === slug);
